"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiWavesOverviewType } from "@/generated/models/ApiWavesOverviewType";
import type { ApiWavesPinFilter } from "@/generated/models/ApiWavesPinFilter";
import { fetchWavesV2Page, getWavesV2OverviewQueryKeyParams } from "@/services/api/waves-v2-api";
export { mapApiWaveToSidebarWave } from "@/services/api/waves-v2-api";
interface UseWavesV2Props {
  readonly overviewType: ApiWavesOverviewType;
  readonly pageSize?: number | undefined;
  readonly following?: boolean | undefined;
  readonly viewerIdentityKey?: string | null | undefined;
  readonly directMessage?: boolean | undefined;
  readonly pinned?: ApiWavesPinFilter | undefined;
  readonly refetchInterval?: number | undefined;
  readonly refetchIntervalInBackground?: boolean | undefined;
}
export const useWavesV2 = t0 => {
  const $ = _c(41);
  const {
    overviewType,
    pageSize: t1,
    following: t2,
    viewerIdentityKey,
    directMessage,
    pinned,
    refetchInterval: t3,
    refetchIntervalInBackground: t4
  } = t0;
  const pageSize = t1 === undefined ? 20 : t1;
  const following = t2 === undefined ? false : t2;
  const refetchInterval = t3 === undefined ? Infinity : t3;
  const refetchIntervalInBackground = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== directMessage || $[1] !== following || $[2] !== overviewType || $[3] !== pageSize || $[4] !== pinned || $[5] !== viewerIdentityKey) {
    t5 = getWavesV2OverviewQueryKeyParams({
      overviewType,
      pageSize,
      following,
      directMessage,
      pinned,
      viewerIdentityKey
    });
    $[0] = directMessage;
    $[1] = following;
    $[2] = overviewType;
    $[3] = pageSize;
    $[4] = pinned;
    $[5] = viewerIdentityKey;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const queryKeyParams = t5;
  const [lastErrorTimestamp, setLastErrorTimestamp] = useState(null);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setLastErrorTimestamp(Date.now());
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleRetryFailure = t6;
  let t7;
  if ($[8] !== queryKeyParams) {
    t7 = [QueryKey.WAVES_V2, queryKeyParams];
    $[8] = queryKeyParams;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== directMessage || $[11] !== following || $[12] !== overviewType || $[13] !== pageSize || $[14] !== pinned) {
    t8 = async t9 => {
      const {
        pageParam
      } = t9;
      return await fetchWavesV2Page({
        page: pageParam,
        pageSize,
        overviewType,
        following,
        directMessage,
        pinned
      });
    };
    $[10] = directMessage;
    $[11] = following;
    $[12] = overviewType;
    $[13] = pageSize;
    $[14] = pinned;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== queryKeyParams) {
    t9 = (previousData, previousQuery) => {
      const previousParams = previousQuery === undefined ? undefined : previousQuery.queryKey[1] as {
        viewer_identity?: string | null;
      } | undefined;
      const previousViewerIdentity = typeof previousParams?.viewer_identity === "string" ? previousParams.viewer_identity : null;
      const currentViewerIdentity = queryKeyParams.viewer_identity ?? null;
      if (previousViewerIdentity === currentViewerIdentity) {
        return previousData;
      }
    };
    $[16] = queryKeyParams;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = getDefaultQueryRetry(handleRetryFailure);
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== refetchInterval || $[20] !== refetchIntervalInBackground || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t11 = {
      queryKey: t7,
      queryFn: t8,
      initialPageParam: 1,
      getNextPageParam: _temp,
      placeholderData: t9,
      refetchInterval,
      refetchIntervalInBackground,
      ...t10
    };
    $[19] = refetchInterval;
    $[20] = refetchIntervalInBackground;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const query = useInfiniteQuery(t11);
  let t12;
  if ($[25] !== query.data?.pages) {
    t12 = query.data?.pages.flatMap(_temp2) ?? [];
    $[25] = query.data?.pages;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const waves = t12;
  let t13;
  if ($[27] !== lastErrorTimestamp || $[28] !== query) {
    t13 = () => {
      if (lastErrorTimestamp !== null && Date.now() - lastErrorTimestamp < 30000) {
        setTimeout(() => {
          query.fetchNextPage().catch(_temp3);
        }, 30000);
        return;
      }
      query.fetchNextPage().catch(_temp4);
    };
    $[27] = lastErrorTimestamp;
    $[28] = query;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const fetchNextPage = t13;
  let t14;
  if ($[30] !== lastErrorTimestamp || $[31] !== query) {
    t14 = () => {
      if (lastErrorTimestamp !== null && Date.now() - lastErrorTimestamp < 30000) {
        setTimeout(() => {
          query.refetch().catch(_temp5);
        }, 30000);
        return;
      }
      query.refetch().catch(_temp6);
    };
    $[30] = lastErrorTimestamp;
    $[31] = query;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const refetch = t14;
  let t15;
  if ($[33] !== fetchNextPage || $[34] !== query.hasNextPage || $[35] !== query.isFetching || $[36] !== query.isFetchingNextPage || $[37] !== query.status || $[38] !== refetch || $[39] !== waves) {
    t15 = {
      waves,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      hasNextPage: query.hasNextPage,
      fetchNextPage,
      status: query.status,
      refetch
    };
    $[33] = fetchNextPage;
    $[34] = query.hasNextPage;
    $[35] = query.isFetching;
    $[36] = query.isFetchingNextPage;
    $[37] = query.status;
    $[38] = refetch;
    $[39] = waves;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
};
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : null;
}
function _temp2(page) {
  return page.waves;
}
function _temp3() {}
function _temp4() {}
function _temp5() {}
function _temp6() {}